/**
 * Campaigns — the list (#281, file #254). Exported for `Campaigns.test.tsx`.
 *
 * Until this screen existed the only way to see a campaign was the report
 * screen's `<select>`, which reads `subject` and `campaignId` and throws away
 * the `status`, `type`, `listId`, `segmentId`, `sent` and `sendAt` that
 * `GET /orgs/{org}/campaigns` already returns. "What has this organization
 * sent, and what is about to go out" had no answer that did not involve
 * picking ids one at a time.
 *
 * NO NEW ROUTE. Both reads already exist and are gated identically
 * (`reports:view`): `GET /orgs/{org}/campaigns` (`campaignsListHandler`) and
 * `GET /orgs/{org}/schedules` (`schedulesListHandler`). This screen is the join
 * of the two, which is what the design's table actually is — see `joinRows`.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import { useAsync } from "../useAsync.js";
import { RefreshButton } from "../RefreshButton.js";
import { relativeTime } from "../time.js";
import { can, type Grant } from "../rbac.js";
import { api, scheduleHasSent, type CampaignRow, type SendScheduleState } from "../api.js";
import { describeSchedule } from "@addressium/domain";
import { oneOffSkipped, oneOffStoppedPartWay } from "./Schedules.js";

/**
 * The two state machines this screen shows at once.
 *
 * `Campaign.status` is intended to say what happened to the send, but is not
 * advanced after scheduling until B3 lands. `SendScheduleState.status`
 * (active/paused/archived)
 * says whether the schedule MAY FIRE — it is the source of truth the launch
 * handler and the sender both gate on. They are not the same fact and neither
 * subsumes the other: the lifecycle record and `counters.sent` establish that a
 * one-off has sent, while a series parent remains `scheduled` and is paused or
 * active according to its lifecycle record. The table shows both columns for
 * that reason, and the Pause/Resume/Archive buttons act on the second one.
 */
export interface CampaignListRow {
  campaign: CampaignRow;
  /** The lifecycle record, when this campaign was scheduled through the console. */
  schedule?: SendScheduleState;
}

const CAMPAIGN_STATUS_COLOR: Record<string, string> = {
  sent: "#15803d",
  sending: "#2563eb",
  scheduled: "#b45309",
  halted: "#b91c1c",
  draft: "#6b7280",
};

/**
 * Present a send status from records that the server actually maintains.
 *
 * B3 will eventually write `Campaign.status = sent`; until then, a completed
 * one-off or a non-zero send counter is the truthful signal. Keep real terminal
 * and in-progress statuses intact so this remains correct after B3 lands.
 */
function campaignStatus(r: CampaignListRow): string {
  const status = r.campaign.status;
  if (status === "draft" || status === "halted" || status === "sending" || status === "sent") return status;
  return r.campaign.sent > 0 || (r.schedule?.kind === "one_off" && scheduleHasSent(r.schedule)) ? "sent" : status;
}

/**
 * Newest first by send time; rows with no send time (drafts, and every
 * recurring parent) sort last.
 *
 * Deliberately a local copy of the Dashboard's comparator rather than an
 * import: `Dashboard.tsx` is pinned by #261's tests and is being edited
 * concurrently, and six lines duplicated is cheaper than two screens contending
 * over one module.
 */
function byMostRecentSend(a: CampaignListRow, b: CampaignListRow): number {
  const at = new Date(a.campaign.sendAt ?? a.campaign.createdAt ?? "").getTime();
  const bt = new Date(b.campaign.sendAt ?? b.campaign.createdAt ?? "").getTime();
  return (Number.isNaN(bt) ? -Infinity : bt) - (Number.isNaN(at) ? -Infinity : at);
}

/**
 * Join the campaign records to their lifecycle records on
 * `campaignId === scheduleId`.
 *
 * That identity is not a guess: `scheduleCampaignHandler` writes both records
 * from the same request and stamps the lifecycle one `scheduleId:
 * body.campaignId` on both the one-off and the recurring branch.
 *
 * LEFT outer, campaigns-driven. A campaign saved as a draft through
 * `POST /campaigns` has never been scheduled and so has no lifecycle row at
 * all; it still belongs in the list, with its actions rendered unavailable
 * rather than offered and broken. Driving from the campaign side also keeps
 * lifecycle rows that have no Campaign item — drip sub-campaigns, re-engagement
 * steps, series editions — out of the table, which is the same rule
 * `campaigns.list` already follows.
 */
export function joinRows(campaigns: CampaignRow[], schedules: SendScheduleState[]): CampaignListRow[] {
  const byId = new Map(schedules.map((s) => [s.scheduleId, s]));
  return campaigns
    .map((campaign) => {
      const schedule = byId.get(campaign.campaignId);
      return schedule ? { campaign, schedule } : { campaign };
    })
    .sort(byMostRecentSend);
}

/** A table row: a campaign, plus — on a series — the editions it sent. */
export type GroupedRow = CampaignListRow & { editions?: CampaignListRow[] };

/** Rows per page, and editions revealed per "show more", on the Campaigns table. */
export const CAMPAIGNS_PAGE_SIZE = 25;
export const EDITIONS_STEP = 10;

/**
 * Fold each series' editions under the series row.
 *
 * Listed flat, an hourly series added 24 rows a day and buried everything else.
 * An edition belongs to the series its `seriesId` names; editions from before
 * #314 carry none, so for those the series whose id prefixes theirs
 * (`<series>-<editionKey>`) is used, longest match first. An edition whose
 * series is not in the list stays a row of its own rather than vanishing.
 */
export function groupEditions(rows: CampaignListRow[]): GroupedRow[] {
  const parents = rows.filter((r) => campaignKind(r) === "series");
  const byId = new Map(parents.map((p) => [p.campaign.campaignId, [] as CampaignListRow[]]));
  const byPrefix = [...byId.keys()].sort((a, b) => b.length - a.length);
  const top: GroupedRow[] = [];
  for (const r of rows) {
    if (r.campaign.type === "series_edition") {
      const parent = r.campaign.seriesId && byId.has(r.campaign.seriesId)
        ? r.campaign.seriesId
        : byPrefix.find((id) => r.campaign.campaignId.startsWith(`${id}-`));
      if (parent) { byId.get(parent)!.push(r); continue; }
    }
    top.push(r);
  }
  // `rows` is newest first, so each parent's editions already are too.
  return top.map((r) => (byId.has(r.campaign.campaignId) ? { ...r, editions: byId.get(r.campaign.campaignId)! } : r));
}

/**
 * One-off or recurring series — read from the LIFECYCLE record, not from
 * `campaign.type`.
 *
 * `CampaignRow.type` cannot express a series and will say "one_off" for one.
 * `saveCampaignSchema.type` is `one_off | series_edition`, and
 * `recordScheduledCampaign` writes `type: existing?.type ?? "one_off"` from
 * BOTH branches of the schedule route — the recurring branch passes no override,
 * because a recurring parent is neither: its EDITIONS are the `series_edition`s
 * and they have no Campaign item by design. So the parent of a daily series is
 * stored as `one_off`, and rendering that field at face value labels every
 * series wrong. `schedule.kind` is written as `"recurring"` by the same request
 * and is the only truthful source.
 *
 * With no lifecycle row there is nothing truthful to say. A campaign becomes a
 * one-off or a series only when it is SCHEDULED — that is the request that
 * writes the lifecycle record — so an unscheduled draft has not chosen yet and
 * the cell says "unscheduled" rather than guessing "one-off" from a stored
 * field that defaults to it.
 */
function campaignKind(r: CampaignListRow): "one-off" | "series" | "edition" | "unscheduled" {
  if (r.schedule) return r.schedule.kind === "recurring" ? "series" : "one-off";
  // One firing of a recurring series: sent by the series' schedule, with no
  // lifecycle record of its own. "unscheduled" read as a draft nobody sent.
  if (r.campaign.type === "series_edition") return "edition";
  return "unscheduled";
}

/**
 * When this campaign fires: a one-off's send time, a series' cron.
 *
 * A recurring parent has NO `schedule.sendAt` — `recordScheduledCampaign`
 * explicitly deletes it, because a series has no single send time — so the cron
 * on the lifecycle record is the only timing a series row has. Rendering "—"
 * for the kind of row that fires most often is exactly the bug #248 fixed on
 * the Schedules screen.
 *
 * Absolute plus relative, in the BROWSER's zone rather than the org's sending
 * zone: an operator deciding whether to pause is reading their own clock.
 */
function campaignWhen(r: CampaignListRow, now?: number): string {
  // A series edition has no send time of its own; when it was written is when it went out.
  const sendAt = r.campaign.sendAt ?? r.schedule?.sendAt ?? r.campaign.createdAt;
  if (sendAt) {
    const at = new Date(sendAt);
    // A stored value that does not parse must not reach the operator as
    // "Invalid Date", which reads as a state of the send rather than a bad row.
    if (Number.isNaN(at.getTime())) return "—";
    const rel = relativeTime(sendAt, now);
    return `${at.toLocaleString()}${rel ? ` (${rel})` : ""}`;
  }
  const cron = r.schedule?.cron;
  if (cron) {
    const desc = describeSchedule(cron, r.schedule?.timezone);
    return `${desc} (${cron})`;
  }
  return "—";
}

/**
 * Who this campaign goes to, as the ids the API actually returns.
 *
 * The design's Audience column shows a recipient COUNT (96,204). Nothing on
 * this route returns one, and no other route computes an audience size for a
 * campaign — so a number here would be invented. The list and segment ids are
 * the real answer to "who does this go to", and the column is headed to say so.
 */
function audience(c: CampaignRow): string {
  const parts = [c.listId, c.segmentId].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

const FILTERS = ["All sends", "Series", "One-off", "Scheduled", "Paused"] as const;
type Filter = (typeof FILTERS)[number];

/**
 * The design's filter chips, each defined against a field that exists.
 *
 * "Ongoing" in the prototype is a series, and "Paused" is a lifecycle state —
 * so the last two chips filter on different records, which is only possible
 * because the rows are joined.
 */
function matchesFilter(r: CampaignListRow, filter: Filter): boolean {
  switch (filter) {
    case "Series": return campaignKind(r) === "series";
    case "One-off": return campaignKind(r) === "one-off";
    // `Campaign.status` is frozen at scheduled until B3. The lifecycle state is
    // what answers the operator's actual question: which sends are still pending?
    case "Scheduled": return r.schedule?.status === "active" && !scheduleHasSent(r.schedule);
    case "Paused": return r.schedule?.status === "paused";
    default: return true;
  }
}

function StatusPill({ status }: { status: string }) {
  return (
    <span style={{ color: CAMPAIGN_STATUS_COLOR[status] ?? "#6b7280", fontWeight: 600 }}>{status}</span>
  );
}

/** COMPLETED gets its own colour here for the same reason it does on Schedules
 *  (#263): "it sent" and "I filed it away" are different answers, and this
 *  table is the other place an operator scans for what is still pending. */
function LifecyclePill({ status }: { status: SendScheduleState["status"] }) {
  const color =
    status === "active" ? "#1b7a3d" : status === "paused" ? "#7a4d00" : status === "completed" ? "#2a4b8d" : "#555";
  const bg =
    status === "active" ? "#d7f0df" : status === "paused" ? "#ffe8a3" : status === "completed" ? "#dde5f6" : "#e2e2e2";
  return (
    <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700, color, background: bg }}>
      {status.toUpperCase()}
    </span>
  );
}

export function Campaigns({
  org,
  grant,
  onCompose,
  onDuplicate,
  onEditSeries,
}: {
  org: string;
  grant: Grant | null;
  onCompose?: () => void;
  /** Open Compose prefilled from this campaign's stored body (#307). */
  onDuplicate?: (campaignId: string) => void;
  onEditSeries?: (campaignId: string) => void;
}) {
  const [filter, setFilter] = useState<Filter>("All sends");
  const [busy, setBusy] = useState("");
  const [actionError, setActionError] = useState("");
  // Bumped by a lifecycle action so the schedules read re-runs: pausing a
  // series changes the lifecycle row, and leaving the table showing the
  // pre-click state is how an operator presses Pause twice.
  const [reloadKey, setReloadKey] = useState(0);
  // Two reads, not N+1. They are independent — neither's result feeds the
  // other's request — so they are separate `useAsync`es and the join happens
  // below once both have landed. Only the schedules read is keyed on
  // `reloadKey`: a lifecycle action cannot change a Campaign record, so
  // re-reading the campaign list after one would be a wasted call.
  const campaigns = useAsync(() => api.campaigns(org), [org]);
  const schedules = useAsync(() => api.schedules(org), [org, reloadKey]);
  const canSchedule = can(grant, "campaigns:schedule", org);

  const rows = useMemo(
    () => joinRows(campaigns.data ?? [], schedules.data ?? []),
    [campaigns.data, schedules.data],
  );
  const shown = useMemo(() => groupEditions(rows).filter((r) => matchesFilter(r, filter)), [rows, filter]);
  const [page, setPage] = useState(0);
  // Which series have their editions open, and how many of each are showing.
  const [openEditions, setOpenEditions] = useState<Record<string, number>>({});
  useEffect(() => setPage(0), [filter]);
  const pages = Math.max(1, Math.ceil(shown.length / CAMPAIGNS_PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const pageRows = shown.slice(current * CAMPAIGNS_PAGE_SIZE, (current + 1) * CAMPAIGNS_PAGE_SIZE);

  const act = async (scheduleId: string, action: "start" | "pause" | "archive") => {
    // Archive is final (the page says so), so it asks first; start and pause are undoable.
    if (action === "archive" && !window.confirm(`Archive "${scheduleId}"? An archived send cannot be restarted.`)) return;
    setBusy(`${scheduleId}:${action}`);
    setActionError("");
    try {
      await api.scheduleLifecycle(org, scheduleId, action);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setActionError(String(e));
    } finally {
      setBusy("");
    }
  };

  // The FIRST load only. A lifecycle action re-reads the schedules, and
  // `useAsync` both flips `loading` back on AND clears `data` for it — so
  // keying the skeleton on `schedules.loading` would throw a spinner over a
  // table that is already on screen and still correct, every time Pause is
  // pressed. `reloadKey > 0` is what separates a reload from the initial read;
  // mid-reload staleness is handled per-cell instead (see the Schedule column).
  const loading = campaigns.loading || (schedules.loading && reloadKey === 0);

  const renderRow = (r: GroupedRow, nested = false) => {
    const editions = r.editions;

                const kind = campaignKind(r);
                const s = r.schedule;
                return (
                  <tr key={r.campaign.campaignId} className={nested ? "edition-row" : undefined} style={nested ? { background: "var(--surface-2, #f8f9fb)" } : undefined}>
                    <td className={nested ? undefined : "t-strong"} style={nested ? { paddingLeft: 24 } : undefined}>
                      {nested && <span className="muted" aria-hidden="true">↳ </span>}
                      {r.campaign.subject} <span className="muted">({r.campaign.campaignId})</span>
                      {!nested && editions && editions.length > 0 && (
                        <div>
                          <button
                            className="btn ghost"
                            style={{ padding: "2px 6px", marginTop: 4 }}
                            aria-expanded={openEditions[r.campaign.campaignId] !== undefined}
                            onClick={() => setOpenEditions((o) => {
                              const next = { ...o };
                              if (next[r.campaign.campaignId] !== undefined) delete next[r.campaign.campaignId];
                              else next[r.campaign.campaignId] = EDITIONS_STEP;
                              return next;
                            })}
                          >
                            {openEditions[r.campaign.campaignId] !== undefined ? "▾ Hide" : "▸ Show"} {editions.length} edition{editions.length === 1 ? "" : "s"}
                          </button>
                        </div>
                      )}
                    </td>
                    <td>{kind}</td>
                    <td><StatusPill status={campaignStatus(r)} /></td>
                    <td>
                      {/* `useAsync` clears `data` on every deps change, so a
                          lifecycle action — which bumps `reloadKey` — leaves
                          `schedules.data` undefined for the length of the
                          re-read. Without this guard every scheduled row would
                          flash "not scheduled" and lose its buttons mid-pause:
                          a false statement about a record that plainly exists,
                          made at the exact moment the operator is watching. */}
                      {s ? <LifecyclePill status={s.status} />
                        : schedules.loading ? <span className="muted">…</span>
                        // A schedules read that FAILED means this row's
                        // lifecycle state is unknown, not absent. Falling
                        // through to "not scheduled" would tell the operator
                        // every scheduled campaign in the org is unscheduled —
                        // the same false statement as the reload flash, on the
                        // path the banner above already admits is degraded.
                        : schedules.error ? <span className="muted" title="Schedules could not be read.">unknown</span>
                        : r.campaign.type === "series_edition" ? <span className="muted">sent by its series</span>
                        : <span className="muted">not scheduled</span>}
                    </td>
                    <td className="muted">{audience(r.campaign)}</td>
                    <td>
                      {/* A recurring PARENT never accumulates: every edition
                          sends under a `<base>-<editionKey>` id with its own
                          `series_edition` record and counters, and nothing rolls
                          up to the parent, so the parent's counter stays at zero
                          forever (#348). Printing "0" next to a daily series that
                          has mailed for months reads as a broken counter, which
                          is precisely the #221 symptom this must not imitate. */}
                      {kind === "series" ? (
                        <span className="muted" title="Editions send under their own ids; the series parent has no aggregate counter.">
                          per edition
                        </span>
                      ) : (
                        r.campaign.sent
                      )}
                    </td>
                    <td className="muted">{campaignWhen(r)}</td>
                    <td>
                      {!s ? (
                        // Mid-reload the row's schedule is merely unread, not
                        // absent — see the Schedule cell above.
                        schedules.loading ? <span className="muted">…</span> : (
                          // No lifecycle record: this campaign has never been
                          // scheduled, so there is no schedule to start, pause
                          // or archive. An enabled button here would post a
                          // `scheduleId` the route has no record for.
                          <span className="muted">—</span>
                        )
                      ) : !canSchedule ? (
                        <span className="muted">read-only</span>
                      ) : (
                        <span style={{ display: "flex", gap: 6 }}>
                          {/* Same gate as Schedules (#263): the domain refuses
                              start and pause on a one-off that has already
                              sent, so offering them here implies a send that
                              has not gone out. This table carries the same
                              controls, so it needs the same rule — including
                              #356's: an archived row is final, and a paused
                              one-off past its send time was skipped, so
                              `transitionSchedule` refuses Start on both. */}
                          <button
                            className="btn ghost"
                            disabled={s.status === "active" || s.status === "archived" || scheduleHasSent(s) || oneOffSkipped(s) || oneOffStoppedPartWay(s) || !!busy}
                            title={oneOffSkipped(s) ? "Its send time has passed; duplicate it to send it." : oneOffStoppedPartWay(s) ? "Its send time has passed." : s.status === "archived" ? "Archived sends are final." : undefined}
                            onClick={() => void act(s.scheduleId, "start")}
                          >
                            Start
                          </button>
                          <button className="btn ghost" disabled={s.status !== "active" || scheduleHasSent(s) || !!busy} onClick={() => void act(s.scheduleId, "pause")}>Pause</button>
                          <button className="btn ghost" disabled={s.status === "archived" || !!busy} onClick={() => void act(s.scheduleId, "archive")}>Archive</button>
                        </span>
                      )}
                      {/* Duplicate is not a lifecycle action, so it sits outside
                          the branch above: a campaign with no schedule record —
                          and one that has already sent, which is the common case
                          — is exactly what an operator wants to copy. */}
                      {onEditSeries && r.schedule?.kind === "recurring" && r.schedule.status !== "archived" && can(grant,"campaigns:manage",org) && <button className="btn ghost" onClick={() => onEditSeries(r.campaign.campaignId)}>Edit series content</button>}
                      {onDuplicate && canSchedule && (
                        <button
                          className="btn ghost"
                          style={{ marginTop: 6 }}
                          onClick={() => onDuplicate(r.campaign.campaignId)}
                          title="Open Compose prefilled from this campaign"
                        >
                          Duplicate
                        </button>
                      )}
                    </td>
                  </tr>
                );
  };

  return (
    <div>
      <div className="pagehead">
        <div><h1>Campaigns</h1><p>One-offs and recurring series. Reporting is per campaign and lifecycle state is authoritative.</p></div>
        {/* One `.row` wrapper, not two children: `.pagehead` is
            `justify-content: space-between`, so a third child would be pushed
            into the middle of the header rather than sitting beside New. */}
        <div className="row">
          {/* Both reads. The table is a join of the two, so refreshing only
              the campaigns would leave every lifecycle badge stale.
              `refetch` keeps the rows on screen: the skeleton below is keyed
              on the FIRST load, and a refresh must not re-trigger it. */}
          <RefreshButton
            refreshing={campaigns.refreshing || schedules.refreshing}
            disabled={loading}
            onClick={() => { void campaigns.refetch(); void schedules.refetch(); }}
          />
          {onCompose && <button className="btn btn-primary" onClick={onCompose}>＋ New campaign</button>}
        </div>
      </div>
      <p className="muted">
        Open and click numbers are on the Campaign report screen. Start, pause and archive act on
        the schedule; nothing is ever deleted. Each firing of a series is an edition with its own
        numbers, listed under its series.
      </p>

      {campaigns.error && <p className="err">{campaigns.error}</p>}
      {/* Reported separately and NOT fatal: the campaign list is the screen, and
          a schedules read that fails should cost the operator the lifecycle
          column and the action buttons, not the whole table. */}
      {schedules.error && (
        <p className="err">
          Schedules could not be loaded, so lifecycle state and actions are unavailable: {schedules.error}
        </p>
      )}
      {actionError && <p className="err">{actionError}</p>}

      <div className="card row" style={{ gap: 8 }}>
        {FILTERS.map((f) => (
          <button
            key={f}
            className={f === filter ? "btn" : "btn ghost"}
            aria-pressed={f === filter}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      {loading && <div className="card"><div className="skeleton sk-kpi" aria-label="Loading…" /></div>}

      {!loading && campaigns.data && rows.length === 0 && (
        <div className="card muted">
          This organization has no campaigns yet. Compose one to see it here.
        </div>
      )}

      {!loading && rows.length > 0 && shown.length === 0 && (
        <div className="card muted">
          {/* "Nothing matches" is a claim about the org's campaigns. With the
              schedules read failed, the Series/One-off/Paused chips are
              filtering on a record that could not be loaded, so an empty
              result is a failure to read and not an answer. */}
          {schedules.error && filter !== "Scheduled"
            ? `Cannot tell which campaigns match “${filter}” — the schedules read failed.`
            : `No campaign matches “${filter}”.`}
        </div>
      )}

      {shown.length > 0 && (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Type</th>
                <th>Status</th>
                <th>Schedule</th>
                {/* "List · segment", not the design's recipient count: no route
                    returns an audience size for a campaign. */}
                <th>Audience (list · segment)</th>
                {/* "Sent", not the design's Open % / Click %: those live on
                    `GET …/campaigns/{id}/report`, one call per campaign, and
                    that route has no bulk form. Two permanently empty columns,
                    or an N+1 on every paint, are both worse than the counter
                    this route does return. */}
                <th>Sent</th>
                <th>When</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const open = openEditions[r.campaign.campaignId];
                const editions = r.editions ?? [];
                return (
                  <Fragment key={r.campaign.campaignId}>
                    {renderRow(r)}
                    {open !== undefined && editions.slice(0, open).map((e) => renderRow(e, true))}
                    {open !== undefined && editions.length > open && (
                      <tr>
                        <td colSpan={8} style={{ paddingLeft: 24 }}>
                          <button className="btn ghost" onClick={() => setOpenEditions((o) => ({ ...o, [r.campaign.campaignId]: open + EDITIONS_STEP }))}>
                            Show {Math.min(EDITIONS_STEP, editions.length - open)} more of {editions.length - open} remaining
                          </button>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {shown.length > CAMPAIGNS_PAGE_SIZE && (
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
              <button className="btn ghost" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous</button>
              <button className="btn ghost" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>Next</button>
              <span className="muted">
                {current * CAMPAIGNS_PAGE_SIZE + 1}–{current * CAMPAIGNS_PAGE_SIZE + pageRows.length} of {shown.length}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="card">
        <div className="muted" style={{ marginBottom: 6 }}>What this table does not show</div>
        <p className="muted" style={{ margin: 0 }}>
          Open and click rates are per campaign and live on the report screen — there is no bulk
          rates route, so they are not columns here. Audience is the list and segment a campaign
          targets, not a recipient count: nothing computes one. A recurring series aggregates no
          counters across its editions; each edition reports on its own.
        </p>
      </div>
    </div>
  );
}
